import { FONT_FAMILY } from "@/constants";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import { Theme } from "@mui/material/styles";
import { ReactNode } from "react";
import { AUTH_SHORTEST } from "./authSpacing";
import {
  Control,
  FieldValues,
  Path,
  useController,
} from "react-hook-form";

export interface AuthInputProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label: string;
  placeholder?: string;
  autoComplete?: string;
  inputType?: "text" | "email" | "password";
  endAdornment?: ReactNode;
  /** Resting hint under the input; replaced by the error message when invalid. */
  helperText?: string;
  /** Extra ids appended to aria-describedby (e.g. a strength meter). */
  describedBy?: string;
  disabled?: boolean;
}

// Scoped to auth screens: always-visible resting border so inputs read on every
// brand palette (DemandScience paper === background), focus ring, error border.
const inputSx = (theme: Theme) => ({
  border: `1px solid ${theme.palette.divider}`,
  transition: theme.transitions.create(["border-color", "box-shadow"], {
    duration: theme.transitions.duration.shorter,
  }),
  "&.Mui-focused": {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
  },
  "&.Mui-error": { borderColor: theme.palette.error.main },
  "&.Mui-error.Mui-focused": {
    boxShadow: `0 0 0 1px ${theme.palette.error.main}`,
  },
  "& input::placeholder": { color: theme.palette.text.secondary, opacity: 1 },
  // Tighter label offset from tablet up (theme default is 36px), tighter still on short laptops.
  [theme.breakpoints.up("sm")]: { "label + &": { marginTop: theme.spacing(4) } },
  // Short laptops: 44px total (42 + border), still the minimum touch target.
  [AUTH_SHORTEST]: {
    "label + &": { marginTop: theme.spacing(3.5) },
    "& input": { minHeight: 42, height: 42 },
  },
});

const labelSx = (theme: Theme) => ({
  "&.Mui-focused": { color: theme.palette.primary.main },
  "&.Mui-error": { color: theme.palette.error.main },
});

export default function AuthInput<T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  autoComplete,
  inputType = "text",
  endAdornment,
  helperText,
  describedBy,
  disabled,
}: AuthInputProps<T>) {
  const { field, fieldState } = useController({ name, control });
  const id = `auth-${name}`;
  const helpId = `${id}-help`;
  const errorMessage = fieldState.error?.message;
  const helpMessage = errorMessage || helperText;
  const describedByIds =
    [helpMessage ? helpId : null, describedBy].filter(Boolean).join(" ") ||
    undefined;

  return (
    <FormControl fullWidth error={!!errorMessage} disabled={disabled}>
      <InputLabel shrink htmlFor={id} sx={labelSx}>
        {label}
      </InputLabel>
      <OutlinedInput
        id={id}
        type={inputType}
        placeholder={placeholder}
        autoComplete={autoComplete}
        endAdornment={endAdornment}
        sx={inputSx}
        inputProps={{
          "aria-invalid": !!errorMessage,
          "aria-describedby": describedByIds,
          spellCheck: false,
          ...(inputType !== "text" ? { autoCapitalize: "none" } : {}),
        }}
        name={field.name}
        value={field.value ?? ""}
        onChange={field.onChange}
        onBlur={field.onBlur}
        inputRef={field.ref}
      />
      {helpMessage && (
        <FormHelperText id={helpId} sx={{ fontFamily: FONT_FAMILY }}>
          {helpMessage}
        </FormHelperText>
      )}
    </FormControl>
  );
}
